/**
 * Attendance React Query Hooks
 * Comprehensive hooks for attendance tracking operations
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { attendanceService } from '../services';

const ATTENDANCE_KEY = 'attendance';
const ATTENDANCE_TODAY_KEY = 'attendance-today';
const ATTENDANCE_HISTORY_KEY = 'attendance-history';
const ATTENDANCE_CURRENTLY_CHECKED_IN_KEY = 'attendance-currently-checked-in';
const ATTENDANCE_REPORTS_KEY = 'attendance-reports';
const ATTENDANCE_REALTIME_KEY = 'attendance-realtime';
const ATTENDANCE_AUDIT_KEY = 'attendance-audit';

// ============================================================================
// CHECK-IN / CHECK-OUT MUTATIONS
// ============================================================================

/**
 * Hook for checking in a student
 */
export function useCheckIn() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: attendanceService.checkIn,
    onSuccess: (_, variables) => {
      // Invalidate today's attendance for the class
      queryClient.invalidateQueries({ 
        queryKey: [ATTENDANCE_TODAY_KEY, variables.classId] 
      });
      // Invalidate currently checked-in list
      queryClient.invalidateQueries({ 
        queryKey: [ATTENDANCE_CURRENTLY_CHECKED_IN_KEY] 
      });
      // Invalidate realtime dashboard
      queryClient.invalidateQueries({ 
        queryKey: [ATTENDANCE_REALTIME_KEY] 
      });
      // Invalidate general attendance queries
      queryClient.invalidateQueries({ 
        queryKey: [ATTENDANCE_KEY] 
      });
    },
  });
}

/**
 * Hook for checking out a student
 */
export function useCheckOut() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: attendanceService.checkOut,
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ 
        queryKey: [ATTENDANCE_TODAY_KEY, variables.classId] 
      });
      queryClient.invalidateQueries({ 
        queryKey: [ATTENDANCE_CURRENTLY_CHECKED_IN_KEY] 
      });
      queryClient.invalidateQueries({ 
        queryKey: [ATTENDANCE_REALTIME_KEY] 
      });
      queryClient.invalidateQueries({ 
        queryKey: [ATTENDANCE_KEY] 
      });
    },
  });
}

/**
 * Hook for bulk check-in
 */
export function useBulkCheckIn() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: attendanceService.bulkCheckIn,
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ 
        queryKey: [ATTENDANCE_TODAY_KEY, variables.classId] 
      });
      queryClient.invalidateQueries({ 
        queryKey: [ATTENDANCE_CURRENTLY_CHECKED_IN_KEY] 
      });
      queryClient.invalidateQueries({ 
        queryKey: [ATTENDANCE_REALTIME_KEY] 
      });
      queryClient.invalidateQueries({ 
        queryKey: [ATTENDANCE_KEY] 
      });
    },
  });
}

/**
 * Hook for bulk check-out
 */
export function useBulkCheckOut() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: attendanceService.bulkCheckOut,
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ 
        queryKey: [ATTENDANCE_TODAY_KEY, variables.classId] 
      });
      queryClient.invalidateQueries({ 
        queryKey: [ATTENDANCE_CURRENTLY_CHECKED_IN_KEY] 
      });
      queryClient.invalidateQueries({ 
        queryKey: [ATTENDANCE_REALTIME_KEY] 
      });
      queryClient.invalidateQueries({ 
        queryKey: [ATTENDANCE_KEY] 
      });
    },
  });
}

// ============================================================================
// DAILY OPERATIONS QUERIES & MUTATIONS
// ============================================================================

/**
 * Hook for getting today's attendance for a class
 * @param {string} classId
 */
export function useTodayAttendance(classId) {
  return useQuery({
    queryKey: [ATTENDANCE_TODAY_KEY, classId],
    queryFn: () => attendanceService.getTodayByClass(classId),
    enabled: !!classId,
    staleTime: 30 * 1000, // 30 seconds - more frequent updates for real-time feel
  });
}

/**
 * Hook for marking a student absent
 */
export function useMarkAbsent() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: attendanceService.markAbsent,
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ 
        queryKey: [ATTENDANCE_TODAY_KEY, variables.classId] 
      });
      queryClient.invalidateQueries({ 
        queryKey: [ATTENDANCE_KEY] 
      });
      // Invalidate student history if we have it cached
      queryClient.invalidateQueries({ 
        queryKey: [ATTENDANCE_HISTORY_KEY, variables.studentId] 
      });
    },
  });
}

/**
 * Hook for marking a student present
 */
export function useMarkPresent() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: attendanceService.markPresent,
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ 
        queryKey: [ATTENDANCE_TODAY_KEY, variables.classId] 
      });
      queryClient.invalidateQueries({ 
        queryKey: [ATTENDANCE_KEY] 
      });
      queryClient.invalidateQueries({ 
        queryKey: [ATTENDANCE_HISTORY_KEY, variables.studentId] 
      });
    },
  });
}

/**
 * Hook for updating an attendance record
 */
export function useUpdateAttendance() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }) => attendanceService.update(id, data),
    onSuccess: () => {
      // Invalidate all attendance-related queries
      queryClient.invalidateQueries({ queryKey: [ATTENDANCE_KEY] });
      queryClient.invalidateQueries({ queryKey: [ATTENDANCE_TODAY_KEY] });
      queryClient.invalidateQueries({ queryKey: [ATTENDANCE_HISTORY_KEY] });
      queryClient.invalidateQueries({ queryKey: [ATTENDANCE_CURRENTLY_CHECKED_IN_KEY] });
      queryClient.invalidateQueries({ queryKey: [ATTENDANCE_REALTIME_KEY] });
    },
  });
}

// ============================================================================
// QUERY HOOKS
// ============================================================================

/**
 * Hook for listing attendance records with filters
 * @param {Object} params - { studentId?, classId?, date?, startDate?, endDate?, status? }
 */
export function useAttendance(params = {}) {
  return useQuery({
    queryKey: [ATTENDANCE_KEY, params],
    queryFn: () => attendanceService.getAll(params),
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
}

/**
 * Hook for getting a student's attendance history
 * @param {string} studentId
 * @param {Object} params - { startDate?, endDate?, classId?, limit? }
 */
export function useStudentAttendanceHistory(studentId, params = {}) {
  return useQuery({
    queryKey: [ATTENDANCE_HISTORY_KEY, studentId, params],
    queryFn: () => attendanceService.getStudentHistory(studentId, params),
    enabled: !!studentId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

/**
 * Hook for getting currently checked-in students
 * @param {Object} params - { classId? }
 */
export function useCurrentlyCheckedIn(params = {}) {
  return useQuery({
    queryKey: [ATTENDANCE_CURRENTLY_CHECKED_IN_KEY, params],
    queryFn: () => attendanceService.getCurrentlyCheckedIn(params),
    staleTime: 30 * 1000, // 30 seconds
  });
}

// ============================================================================
// REPORT QUERIES
// ============================================================================

/**
 * Hook for daily attendance report
 * @param {Object} params - { date?, classId? }
 */
export function useDailyAttendanceReport(params = {}) {
  return useQuery({
    queryKey: [ATTENDANCE_REPORTS_KEY, 'daily', params],
    queryFn: () => attendanceService.getDailyReport(params),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

/**
 * Hook for weekly attendance report
 * @param {Object} params - { weekStart?, classId? }
 */
export function useWeeklyAttendanceReport(params = {}) {
  return useQuery({
    queryKey: [ATTENDANCE_REPORTS_KEY, 'weekly', params],
    queryFn: () => attendanceService.getWeeklyReport(params),
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Hook for monthly attendance report
 * @param {Object} params - { month?, year?, classId? }
 */
export function useMonthlyAttendanceReport(params = {}) {
  return useQuery({
    queryKey: [ATTENDANCE_REPORTS_KEY, 'monthly', params],
    queryFn: () => attendanceService.getMonthlyReport(params),
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Hook for late arrivals report
 * @param {Object} params - { date?, classId?, scheduledTime? }
 */
export function useLateArrivalsReport(params = {}) {
  return useQuery({
    queryKey: [ATTENDANCE_REPORTS_KEY, 'late-arrivals', params],
    queryFn: () => attendanceService.getLateArrivals(params),
    staleTime: 2 * 60 * 1000,
  });
}

/**
 * Hook for early departures report
 * @param {Object} params - { date?, classId?, scheduledEndTime? }
 */
export function useEarlyDeparturesReport(params = {}) {
  return useQuery({
    queryKey: [ATTENDANCE_REPORTS_KEY, 'early-departures', params],
    queryFn: () => attendanceService.getEarlyDepartures(params),
    staleTime: 2 * 60 * 1000,
  });
}

/**
 * Hook for attendance summary statistics
 * @param {Object} params - { startDate, endDate, classId? }
 */
export function useAttendanceSummary(params = {}) {
  return useQuery({
    queryKey: [ATTENDANCE_REPORTS_KEY, 'summary', params],
    queryFn: () => attendanceService.getSummary(params),
    enabled: !!(params.startDate && params.endDate),
    staleTime: 5 * 60 * 1000,
  });
}

// ============================================================================
// REAL-TIME / DASHBOARD QUERIES
// ============================================================================

/**
 * Hook for real-time dashboard data
 */
export function useRealtimeDashboard() {
  return useQuery({
    queryKey: [ATTENDANCE_REALTIME_KEY],
    queryFn: () => attendanceService.getRealtimeDashboard(),
    staleTime: 30 * 1000, // 30 seconds
    refetchInterval: 60 * 1000, // Auto-refetch every minute
  });
}

/**
 * Hook for getting Supabase realtime subscription configuration
 */
export function useRealtimeConfig() {
  return useQuery({
    queryKey: [ATTENDANCE_REALTIME_KEY, 'config'],
    queryFn: () => attendanceService.getRealtimeConfig(),
    staleTime: 60 * 60 * 1000, // 1 hour - config rarely changes
  });
}

// ============================================================================
// AUDIT QUERIES
// ============================================================================

/**
 * Hook for getting audit log for an attendance record
 * @param {string} attendanceId
 */
export function useAttendanceAuditLog(attendanceId) {
  return useQuery({
    queryKey: [ATTENDANCE_AUDIT_KEY, attendanceId],
    queryFn: () => attendanceService.getAuditLog(attendanceId),
    enabled: !!attendanceId,
    staleTime: 5 * 60 * 1000,
  });
}
